import { getDefaultState } from './state';
import { defineStore } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import bff from '@/shared/api/bff';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { getAuthClient } from '@/plugins/auth';
import type { IGlobalError } from '@/stores/global/globalStore.model.ts';
import { CHANNELS } from '@/shared/models/constants.models.ts';
import type {
  IRequestChallenge,
  IUpdatePassword,
  IUpdateUserID,
  IUserIdChallenge,
} from '@/modules/home/store/homeStore.model.ts';
import type { AxiosError } from 'axios';
import type { ICustomerStoreCustomerProfile } from '@/stores/customer/customerStore.model.ts';

export const useHomeStore = defineStore('home', {
  state: getDefaultState,
  actions: {
    setMfaToken(value: string) {
      this.mfaToken = value;
    },
    setLoading(value: boolean) {
      this.loading = value;
    },
    resetState() {
      Object.assign(this, getDefaultState());
    },
    async requestChallenge(request: IRequestChallenge) {
      const globalStore = useGlobalStore();
      globalStore.submitLoading = true;
      try {
        const { locale } = request;
        delete request.locale;

        // Validating User credential
        const { token } = request;
        delete request.token;
        const challengeResult = await bff.requestUserIdChallenge({
          loginId: request.loginId,
          locale,
          token,
        });
        if (challengeResult.status === 200) {
          globalStore.addAlert({
            type: 'success',
            details: { key: 'L_verification_code_sent' },
            context: 'myaccount',
          });
          return challengeResult.data?.mfaToken;
        }
      } catch (e) {
        globalStore.addError({ error: e, context: 'myaccount' } as IGlobalError);
        globalStore.submitLoading = false;
        throw e;
      } finally {
        globalStore.submitLoading = false;
      }
    },
    async updateUserID(request: IUpdateUserID) {
      const globalStore = useGlobalStore();
      globalStore.submitLoading = true;
      const customerStore = useCustomerStore();
      try {
        await bff.updateUserID(request);
        customerStore.updateCustomerProfile({
          email: request.loginId,
        } as ICustomerStoreCustomerProfile);
        return true;
      } catch (e: unknown) {
        const err = e as AxiosError;
        globalStore.submitLoading = false;
        const status = err.response?.status;
        if (status === 400) {
          throw e;
        }
        if (status === 423) {
          setTimeout(async () => {
            await getAuthClient().logout();
          }, 2000);
        }
        const keyMsg = this.userIdErrorMessage(status);
        if (keyMsg) {
          globalStore.addError({ error: { key: keyMsg }, context: 'myaccount' } as IGlobalError);
        } else {
          globalStore.addError({ error: e, context: 'myaccount' } as IGlobalError);
        }
        return false;
      } finally {
        globalStore.submitLoading = false;
      }
    },
    userIdErrorMessage(status: number | undefined) {
      switch (status) {
        case 200:
          return 'L_update_user_id_success';
        case 403:
          return 'L_update_user_id_password_does_not_match';
        case 409:
          return 'L_update_user_id_error_exists';
        case 423:
          return 'L_update_user_id_account_locked';
        default:
          return '';
      }
    },
    async updatePassword(request: IUpdatePassword) {
      const globalStore = useGlobalStore();
      try {
        await bff.updatePassword(request);
        globalStore.addAlert({
          type: 'success',
          details: { key: 'L_update_password_success' },
          context: 'myaccount',
        });
        return true;
      } catch (e: unknown) {
        const err = e as AxiosError<{ appStatusCode: number } | null>;
        const keyMsg = this.passwordErrorMessage(err.response?.data?.appStatusCode);
        if (keyMsg) {
          globalStore.addError({ error: { key: keyMsg }, context: 'myaccount' } as IGlobalError);
        } else {
          globalStore.addError({ error: e, context: 'myaccount' } as IGlobalError);
        }
        if (err.response?.status === 423) {
          await getAuthClient().logout();
        }
        throw e;
      }
    },
    passwordErrorMessage(errorCode: number | undefined) {
      switch (errorCode) {
        case 40310:
          return 'L_password_entered_does_not_match_records';
        case 40311:
          return 'L_update_password_error_no_of_attemps';
        default:
          return '';
      }
    },
    async requestUserIdChallenge(request: IUserIdChallenge) {
      const globalStore = useGlobalStore();
      globalStore.submitLoading = true;

      try {
        // const verificationResult =
        await bff.changeUserVerification(request);
        // if (verificationResult?.status === 200) {
        //   // This
        // }
      } catch (e: unknown) {
        const err = e as AxiosError<{ appStatusCode: string }>;
        const statusCode = err.response?.data.appStatusCode;
        if (
          err.response?.status === 403 &&
          (statusCode === 'dcp-5000' || statusCode === 'dcp-5002')
        ) {
          const statusMessage =
            statusCode === 'dcp-5000'
              ? 'L_invalid_password_update_user_id'
              : 'L_user_already_in_use_update_user_id';
          globalStore.addError({
            error: { key: statusMessage },
            context: 'myaccount',
          } as IGlobalError);
        } else {
          globalStore.addError({ error: e, context: 'myaccount' } as IGlobalError);
        }
        globalStore.submitLoading = false;
        throw e;
      } finally {
        globalStore.submitLoading = false;
      }
    },
    async createChallenge(challengeSpec: {
      viewContext: string;
      payload: { channel: CHANNELS; phone?: string; email?: string };
    }) {
      const globalStore = useGlobalStore();
      try {
        const result = await bff.createChallenge(challengeSpec.payload);
        this.setMfaToken(result.data.mfaToken);
      } catch (e) {
        globalStore.addError({ error: e, context: challengeSpec.viewContext } as IGlobalError);
        throw e;
      }
    },
    async updateMobileNumber(updateSpec: {
      viewContext: string;
      payload: { mobileNumber: string };
    }) {
      const globalStore = useGlobalStore();
      try {
        await bff.updateMobileNumber(updateSpec.payload);
      } catch (e) {
        globalStore.addError({ error: e, context: updateSpec.viewContext } as IGlobalError);
        throw e;
      }
    },
  },
});
